import { useEffect, useState } from "react";
import { Header, Footer } from "@/components/Header";
import { LookCard } from "@/components/LookCard";
import { api, mediaUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function LookbookPage() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/lookbook").then((r) => {
      setLooks(r.data);
      setLoading(false);
    });
  }, []);

  const [hero, ...rest] = looks;

  return (
    <div className="min-h-screen bg-[#0B0B0C] relative z-[2]">
      <Header />

      {/* Hero */}
      <section className="relative pt-20 min-h-[92vh] flex items-end overflow-hidden">
        {hero && (
          <img
            src={mediaUrl(hero.hero_thumbnail)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/40 to-[#0B0B0C]/70" />
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 pb-24 w-full">
          <p className="overline mb-6 fade-up">The Luxbel Film Archive</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tight max-w-4xl fade-up" style={{ animationDelay: "0.1s" }}>
            Fashion, traced from motion.
          </h1>
          <p className="text-[#8E8E9A] text-base lg:text-lg font-light mt-8 max-w-xl leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
            Each film is dissected into looks and products — a living editorial catalogue where every garment carries its own page.
          </p>
          <div className="flex gap-6 mt-10 fade-up" style={{ animationDelay: "0.3s" }}>
            {hero && (
              <Link
                to={`/looks/${hero.id}`}
                data-testid="hero-view-look-button"
                className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-[#C5A059] transition-colors duration-300"
              >
                Enter the Lookbook
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            )}
            <Link
              to="/products"
              data-testid="hero-browse-products-button"
              className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 rounded-full text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </section>

      {/* Look grid */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between mb-12 border-b border-white/10 pb-6">
          <div>
            <p className="overline mb-3">Collections</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-light">The Looks</h2>
          </div>
          <span className="font-mono text-xs text-[#8E8E9A]">{looks.length} FILMS</span>
        </div>

        {loading ? (
          <div className="overline animate-pulse">Loading looks…</div>
        ) : looks.length === 0 ? (
          <p className="text-[#8E8E9A] font-light">No published looks yet. Check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {rest.map((l, i) => (
              <LookCard key={l.id} look={l} index={i} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
